import { useEffect, useState } from "react";
import api from "@/lib/api";
import logger from "@/lib/logger";
import { ArrowUpRight, ArrowDownLeft, Send, History } from "lucide-react";

const LIMIT = 5;

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function Row({ transfer, kind }) {
    const isSent = kind === "sent";
    const other = isSent ? transfer.to_first_name : transfer.from_first_name;
    const Icon = isSent ? ArrowUpRight : ArrowDownLeft;
    const color = isSent ? "text-slate-600" : "text-emerald-700";
    const bg = isSent ? "bg-slate-100" : "bg-emerald-50";
    return (
        <div className="flex items-start gap-3 py-3" data-testid={`recent-${kind}-row`}>
            <div className={`h-9 w-9 ${bg} ${color} rounded-full grid place-items-center shrink-0`}>
                <Icon className="h-4 w-4" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-mono text-sm font-bold text-slate-900">
                        {isSent ? "-" : "+"}{transfer.amount}
                    </span>
                    <span className="text-sm text-slate-700">{isSent ? "to" : "from"}</span>
                    <span className="text-sm font-semibold text-slate-900">{other}</span>
                </div>
                {transfer.message && (
                    <p className="text-xs text-slate-500 italic truncate mt-0.5" title={transfer.message}>
                        "{transfer.message}"
                    </p>
                )}
            </div>
            <span className="text-[10px] uppercase tracking-[0.14em] font-mono text-slate-400 shrink-0 mt-1">
                {timeAgo(transfer.created_at)}
            </span>
        </div>
    );
}

function Empty({ kind }) {
    return (
        <div className="text-sm text-slate-500 italic py-6 text-center" data-testid={`recent-${kind}-empty`}>
            {kind === "sent" ? "You haven't sent any credits yet." : "You haven't received any credits yet."}
        </div>
    );
}

export default function RecentTransfers({ refreshKey = 0 }) {
    const [data, setData] = useState({ sent: [], received: [] });
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/transfers/me");
                if (!cancelled) {
                    setData({
                        sent: (r.data?.sent || []).slice(0, LIMIT),
                        received: (r.data?.received || []).slice(0, LIMIT),
                    });
                }
            } catch (e) {
                logger.warn("RecentTransfers", "could not load:", e?.message || e);
            } finally {
                if (!cancelled) setLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, [refreshKey]);

    if (!loaded) return null;
    const nothing = data.sent.length === 0 && data.received.length === 0;
    if (nothing) return null;

    return (
        <div className="mt-6 bg-white border border-slate-200 rounded-md p-6 md:p-8" data-testid="recent-transfers-card">
            <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-slate-700" strokeWidth={1.75} />
                <h3 className="font-heading text-base font-bold text-slate-900">Recent activity</h3>
            </div>
            <p className="text-sm text-slate-500 mt-1">Your last {LIMIT} sent and {LIMIT} received transfers. Names only — never email addresses.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 mt-4">
                <div>
                    <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                        <Send className="h-3.5 w-3.5 text-slate-500" strokeWidth={1.75} />
                        <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-slate-500">Sent</span>
                    </div>
                    <div className="divide-y divide-slate-100" data-testid="recent-sent">
                        {data.sent.length === 0
                            ? <Empty kind="sent" />
                            : data.sent.map((t) => <Row key={t.id} transfer={t} kind="sent" />)
                        }
                    </div>
                </div>
                <div>
                    <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                        <ArrowDownLeft className="h-3.5 w-3.5 text-emerald-700" strokeWidth={1.75} />
                        <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-slate-500">Received</span>
                    </div>
                    <div className="divide-y divide-slate-100" data-testid="recent-received">
                        {data.received.length === 0
                            ? <Empty kind="received" />
                            : data.received.map((t) => <Row key={t.id} transfer={t} kind="received" />)
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}
